import { apiService } from "../services/apiService";
import { useAuth } from "../contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Checkbox } from 'semantic-ui-react';
import io from 'socket.io-client';
import config from '../config';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import StreamingThemePicker from '../Components/StreamingThemePicker';
import ScoreboardAppearanceEditor from '../Components/ScoreboardAppearanceEditor';
import { normalizePlayerNameSize } from '../utils/streamingTypography';

const GameDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [streamingOpen, setStreamingOpen] = useState(false);
    const [gameSettingsOpen, setGameSettingsOpen] = useState(false);
    const [players, setPlayers] = useState([]);
    const [game, setGame] = useState({});
    const [isLinkedGame, setIsLinkedGame] = useState(false);
    const [linkedGame, setLinkedGame] = useState({});
    const [tables, setTables] = useState([]);
    const [relatedGames, setRelatedGames] = useState([]);
    const [gameName, setGameName] = useState('');
    const [gameType, setGameType] = useState('');
    const [gameDate, setGameDate] = useState('');
    const [gameStatus, setGameStatus] = useState('');
    const [gameTable, setGameTable] = useState('');
    const [gameHasFlags, setGameHasFlags] = useState(false);
    const [gameIsRace, setGameIsRace] = useState(false);
    const [gameRaceTo, setGameRaceTo] = useState(2);
    const [gameFirstPlayer, setGameFirstPlayer] = useState('');
    const [gameSecondPlayer, setGameSecondPlayer] = useState('');
    const [linkedGameId, setLinkedGameId] = useState('');
    const [tournamentTitle, setTournamentTitle] = useState('');
    const [displayBreaks, setDisplayBreaks] = useState('false');
    const [streamingTheme, setStreamingTheme] = useState('welshopen');
    const [playerNameFormat, setPlayerNameFormat] = useState('full');
    const [playerNameSize, setPlayerNameSize] = useState(100);
    const [streamingStatsMode, setStreamingStatsMode] = useState('auto');
    const [useStreamingSettings, setUseStreamingSettings] = useState(false);
    const [gameSettings, setGameSettings] = useState({});
    const [useGameSettings, setUseGameSettings] = useState(false);
    const [loading, setLoading] = useState(true);
    const [player1FramesWon, setPlayer1FramesWon] = useState(0);
    const [player2FramesWon, setPlayer2FramesWon] = useState(0);
    const [isAndroidTV, setIsAndroidTV] = useState(false);
    const [socket, setSocket] = useState(null);

    const GAME_TYPES = {
        snooker: { enabledKey: "snooker_shot_timer_enabled" },
        pool: {
            enabledKey: "pool_shot_timer_enabled",
            shotKey: "pool_shot_time",
            extKey: "pool_extension_time",
        },
        us_pool: {
            enabledKey: "us_pool_shot_timer_enabled",
            shotKey: "us_pool_shot_time",
            extKey: "us_pool_extension_time",
        },
        heyball: {
            enabledKey: "chinese_pool_shot_timer_enabled",
            shotKey: "chinese_pool_shot_time",
            extKey: "chinese_pool_extension_time",
        },
    };

    useEffect(() => {
        toastr.options = {
            positionClass: 'toast-top-right',
            timeOut: 3000,
            extendedTimeOut: 1000,
            closeButton: true,
            progressBar: true,
            showMethod: 'fadeIn',
            hideMethod: 'fadeOut',
            showDuration: 300,
            hideDuration: 1000
        };

        const ua = navigator.userAgent.toLowerCase();
        const isAndroid = ua.indexOf("android") > -1;
        if (isAndroid) {
            setIsAndroidTV(true);
        }
    }, []);

    const copyToClipboard = async (text) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                textArea.remove();
            }
            toastr.success('Link copied to clipboard!');
        } catch (err) {
            toastr.error('Failed to copy link');
        }
    };

    const getClubGame = async () => {
        const data = await apiService.post('/club/game-link', { link: id });
        setGame(data.game);
        setIsLinkedGame(data.is_linked_game);
        if (data.is_linked_game) {
            setLinkedGame(data.linked_game);
            setLinkedGameId(data.game.linked_game_id || '');
        } else {
            setLinkedGame({});
            setLinkedGameId('');
        }
    };

    const getTableGames = async (id_user, game_link) => {
        try {
            const data = await apiService.post('/club/table-games', { id_user, game_link });
            setRelatedGames(data.games || []);
        } catch (e) {
            console.error('Failed to fetch related games:', e);
            setRelatedGames([]);
        }
    };

    const getStreamingSettings = async (table_id) => {
        try {
            const data = await apiService.post('/table/streaming-settings', { table_id });
            if (data.success && data.streaming_settings) {
                const raw = data.streaming_settings;
                const settings = typeof raw === 'string' ? JSON.parse(raw) : raw;
                setTournamentTitle(settings.tournament_title || '');
                setDisplayBreaks(String(settings.display_breaks) === 'true' ? 'true' : 'false');
                setStreamingTheme(settings.streaming_theme || 'welshopen');
                setPlayerNameFormat(settings.player_name_format === 'last' ? 'last' : 'full');
                setPlayerNameSize(normalizePlayerNameSize(settings.player_name_size));
                setStreamingStatsMode(['auto', 'manual', 'always'].includes(settings.streaming_stats_mode) ? settings.streaming_stats_mode : 'auto');
                setUseStreamingSettings(settings.use_streaming_settings === true || settings.use_streaming_settings === 'true');
            } else {
                console.error('Invalid streaming settings response:', data);
            }
        } catch (e) {
            console.error('Failed to fetch streaming settings:', e);
        }
    };

    const getGameSettings = async (table_id) => {
        try {
            const data = await apiService.post('/table/game-settings', { table_id });
            if (data.success && data.game_settings) {
                const settings = typeof data.game_settings === 'string' ? JSON.parse(data.game_settings) : data.game_settings;
                setGameSettings(settings);
                setUseGameSettings(settings.use_game_settings === true || settings.use_game_settings === 'true');
            } else {
                console.error('Invalid game settings response:', data);
                setGameSettings({});
                setUseGameSettings(false);
            }
        } catch (e) {
            console.error('Failed to fetch game settings:', e);
            setGameSettings({});
            setUseGameSettings(false);
        }
    };

    const updateStreamingSettings = async () => {
        try {
            const settings = {
                tournament_title: tournamentTitle,
                display_breaks: displayBreaks,
                streaming_theme: streamingTheme,
                player_name_format: playerNameFormat,
                player_name_size: normalizePlayerNameSize(playerNameSize),
                streaming_stats_mode: streamingStatsMode,
                use_streaming_settings: useStreamingSettings
            };
            const result = await apiService.post('/table/update-streaming-settings', { table_id: game.id_table, settings });
            if (result.success === false) throw new Error(result.message || 'Streaming settings could not be saved');
            setStreamingOpen(false);
            toastr.success('Streaming settings updated successfully!');
        } catch (e) {
            console.error('Failed to update streaming settings:', e);
            toastr.error('Failed to update streaming settings');
        }
    };

    const updateGameSettings = async () => {
        try {
            const settings = {
                ...gameSettings,
                use_game_settings: useGameSettings
            };
            await apiService.post('/table/update-game-settings', { table_id: game.id_table, settings });
            setGameSettingsOpen(false);
            toastr.success('Game settings updated successfully!');
        } catch (e) {
            console.error('Failed to update game settings:', e);
            toastr.error('Failed to update game settings');
        }
    };

    const editGame = async () => {
        if (game.creation_type === 'created' && !gameTable) {
            toastr.warning('Please select a table for the game.');
            return;
        }
        try {
            const payload = {
                id: id,
                name: gameName,
                type: gameType,
                date: gameDate,
                status: gameStatus,
                has_flags: gameHasFlags ? '1' : '0',
                is_race: gameIsRace ? '1' : '0',
                race_to: gameIsRace ? gameRaceTo : null,
                first_player: gameFirstPlayer,
                second_player: gameSecondPlayer,
            };
            if (game.creation_type === 'created') {
                payload.id_table = gameTable;
            }
            if (game.creation_type === 'default') {
                payload.linked_game_id = linkedGameId || null;
            }
            await apiService.post('/club/game/edit', payload);
            await getClubGame();
            setOpen(false);
            toastr.success('Game updated successfully!');
            if (socket && socket.connected) {
                // Determine which ID to refresh. If linked, we only need to refresh the linked one
                // as the current page (scoreboard) will reload based on link changes anyway
                const mainRefreshId = isLinkedGame && linkedGame?.id ? linkedGame.id : game.id;
                socket.emit('refreshGame', mainRefreshId);

                // If we JUST linked a game that is different from previous, we might want to refresh both
                // but usually the linkedGameId update is enough to trigger a sync
            }
        } catch (e) {
            toastr.error(e.response?.data?.message || 'Failed to update game');
        }
    };

    const handleOpenEditPopup = () => {
        if (game.creation_type === 'default' && linkedGameId) {
            const linkedGame = relatedGames.find(g => String(g.id) === String(linkedGameId));
            if (!linkedGame) {
                toastr.error('Linked game not found. Please select a valid game.');
                return;
            }
        }
        setOpen(true);
    };

    const handleRefreshGame = () => {
        if (socket && socket.connected) {
            const gameId = isLinkedGame ? linkedGame.id : game.id;
            socket.emit('refreshGame', gameId);
            toastr.success('Refresh signal sent to all connected devices!');
        } else {
            toastr.error('Socket not connected. Please try again.');
        }
    };

    useEffect(() => {
        if (game.creation_type === 'default' && linkedGameId && relatedGames.length > 0) {
            const linkedGame = relatedGames.find(g => String(g.id) === String(linkedGameId));
            if (linkedGame) {
                setGameName(linkedGame.name || '');
                setGameType(linkedGame.type || '');
                setGameDate(linkedGame.date ? new Date(linkedGame.date).toISOString().split('T')[0] : '');
                setGameStatus(linkedGame.status || '');
                setGameHasFlags(linkedGame.has_flags == 1 || linkedGame.has_flags === '1');
                setGameIsRace(linkedGame.is_race == 1 || linkedGame.is_race === '1');
                setGameRaceTo(linkedGame.race_to || 2);
                setGameFirstPlayer(linkedGame.first_player || '');
                setGameSecondPlayer(linkedGame.second_player || '');
            } else {
                toastr.warning('Selected linked game not found, displaying default game data');
                setGameName(game.name || '');
                setGameType(game.type || '');
                setGameDate(game.date ? new Date(game.date).toISOString().split('T')[0] : '');
                setGameStatus(game.status || '');
                setGameTable(game.creation_type === 'created' ? game.id_table : tables[0]?.id || '');
                setGameHasFlags(game.has_flags == 1 || game.has_flags === '1');
                setGameIsRace(game.is_race == 1 || game.is_race === '1');
                setGameRaceTo(game.race_to || 2);
                setGameFirstPlayer(game.first_player || '');
                setGameSecondPlayer(game.second_player || '');
            }
        } else {
            setGameName(game.name || '');
            setGameType(game.type || '');
            setGameDate(game.date ? new Date(game.date).toISOString().split('T')[0] : '');
            setGameStatus(game.status || '');
            setGameTable(game.creation_type === 'created' ? game.id_table : tables[0]?.id || '');
            setGameHasFlags(game.has_flags == 1 || game.has_flags === '1');
            setGameIsRace(game.is_race == 1 || game.is_race === '1');
            setGameRaceTo(game.race_to || 2);
            setGameFirstPlayer(game.first_player || '');
            setGameSecondPlayer(game.second_player || '');
        }
    }, [game, tables, linkedGameId, relatedGames]);

    useEffect(() => {
        if (user) {
            setLoading(true);
            const fetchData = async () => {
                try {
                    const [tablesData, playersData, gameData] = await Promise.all([
                        apiService.post('/club/tables', { id: user.id }),
                        apiService.post('/club/game/players', { id: user.id }),
                        apiService.post('/club/game-link', { link: id })
                    ]);
                    setTables(tablesData.tables);
                    setPlayers(playersData.players);
                    setGame(gameData.game);
                    setIsLinkedGame(gameData.is_linked_game);
                    if (gameData.is_linked_game) {
                        setLinkedGame(gameData.linked_game);
                        setLinkedGameId(gameData.game.linked_game_id || '');
                    } else {
                        setLinkedGame({});
                        setLinkedGameId('');
                    }
                    await Promise.all([
                        getStreamingSettings(gameData.game.id_table),
                        getGameSettings(gameData.game.id_table)
                    ]);
                } catch (e) {
                    console.error('Failed to fetch initial data:', e);
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
        }
    }, [user, id]);

    useEffect(() => {
        if (user && game.creation_type === 'default' && game.type) {
            getTableGames(user.id, id);
        } else {
            setRelatedGames([]);
        }
    }, [user, game.creation_type, game.type, id]);

    useEffect(() => {
        if (streamingOpen && game.id_table) {
            getStreamingSettings(game.id_table);
        }
    }, [streamingOpen, game.id_table]);

    useEffect(() => {
        if (gameSettingsOpen && game.id_table) {
            getGameSettings(game.id_table);
        }
    }, [gameSettingsOpen, game.id_table]);

    useEffect(() => {
        let socketGameId = isLinkedGame ? linkedGame.id : game.id;
        const socketUrl = config.SOCKET_URL;
        const newSocket = io(socketUrl, {
            transports: ['polling', 'websocket'],
            timeout: 20000,
            forceNew: true,
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: 5,
            maxReconnectionAttempts: 5
        });
        setSocket(newSocket);
        newSocket.on('connect', () => {
            newSocket.emit('joinGame', socketGameId);
            newSocket.emit('requestCurrentState');
        });

        newSocket.on('connect_error', (error) => {
            console.error('Socket connect error:', error);
        });

        newSocket.on('disconnect', (reason) => {
            console.log('Socket disconnected:', reason);
        });

        newSocket.on('gameStateUpdate', (incomingState) => {
            if (incomingState.id === socketGameId) {
                setPlayer1FramesWon(incomingState.first_player_frames || 0);
                setPlayer2FramesWon(incomingState.second_player_frames || 0);
            }
        });

        newSocket.on('initialGameState', (incomingState) => {
            if (incomingState && incomingState.id === socketGameId) {
                setPlayer1FramesWon(incomingState.first_player_frames || 0);
                setPlayer2FramesWon(incomingState.second_player_frames || 0);
            }
        });

        return () => {
            newSocket.emit('leaveGame', socketGameId);
            newSocket.disconnect();
        };
    }, [game?.id, isLinkedGame]);

    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="page-body">
                    <div className="container-xl w-100">
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const isDefaultGameWithLinked = game.creation_type === 'default' && linkedGameId;

    return (
        <div className={`page-wrapper ${isAndroidTV ? 'android-tv-layout' : ''}`}>
            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle">
                                <svg className="w-4 h-4 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" onClick={() => navigate('/games')}>
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12l4-4m-4 4l4 4" />
                                </svg>
                                <span className="cursor-pointer text-lg text-gray-800" onClick={() => navigate('/games')}>Back</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="page-body">
                <div className="container-xl w-100">
                    <h1 className="match-heading text-center">Match details - {isLinkedGame ? linkedGame.name : game.name}</h1>
                    <div className="match-score d-flex justify-content-around align-items-center">
                        <div className="player text-2xl cursor-pointer" style={{ fontSize: 24 }} onClick={() => { navigate(`/player/${game.first_player}`) }}>{isLinkedGame ? linkedGame.first_player_name : game.first_player_name}</div>
                        <div className="score">{player1FramesWon} - {player2FramesWon}</div>
                        <div className="player text-2xl cursor-pointer" style={{ fontSize: 24 }} onClick={() => { navigate(`/player/${game.second_player}`) }}>{isLinkedGame ? linkedGame.second_player_name : game.second_player_name}</div>
                    </div>
                    {game.type === 'snooker' && <details className="mb-4">
                        <summary className="btn btn-outline-primary d-inline-flex align-items-center gap-2">
                            <span>Scoreboard design · Change appearance</span>
                            <span className="badge sb-new-flag">New</span>
                        </summary>
                        <ScoreboardAppearanceEditor game={isLinkedGame && linkedGame.id ? linkedGame : game} />
                    </details>}
                    <div className="row row-deck row-cards">
                        <div className="col-12 col-sm-6 col-md-3">
                            <div className="card cursor-pointer position-relative">
                                <div className="card-body text-center d-flex justify-content-center align-items-center position-relative">
                                    <div className="d-flex flex-column align-items-center" onClick={() => navigate(`/play/${game.type}/${game.link}/scoreboard`)}>
                                        <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 16H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v1M9 12H4m8 8V9h8v11h-8Zm0 0H9m8-4a1 1 0 1 0-2 0 1 1 0 0 0 2 0Z" />
                                        </svg>
                                        <span className="action-name">Scoreboard</span>
                                    </div>
                                    <svg
                                        className="position-absolute cursor-pointer"
                                        style={{ top: '8px', right: '8px', width: '20px', height: '20px' }}
                                        aria-hidden="true"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                        onClick={() => setGameSettingsOpen(true)}
                                    >
                                        <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97 0-.33-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.31-.61-.22l-2.49 1c-.52-.39-1.06-.73-1.69-.98l-.37-2.65A.506.506 0 0 0 14 2h-4c-.25 0-.46.18-.5.42l-.37 2.65c-.63.25-1.17.59-1.69.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.34-.07.67-.07 1 0 .33.03.65.07.97L2.46 14.6c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.31.61.22l2.49-1c.52.39 1.06.73 1.69.98l.37 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.37-2.65c.63-.25 1.17-.59 1.69-.98l2.49 1c.22.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66Z" />
                                    </svg>
                                </div>
                                <div className="card-btn d-flex">
                                    <span className="w-50" onClick={() => navigate(`/play/${game.type}/${game.link}/scoreboard`)}>Enter</span>
                                    <span className="w-50" onClick={() => copyToClipboard(`${window.location.origin}/play/${game.type}/${game.link}/scoreboard`)}>Copy link</span>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3">
                            <div className="card cursor-pointer">
                                <div className="card-body text-center" onClick={() => navigate(`/play/${game.type}/${game.link}/referee`)}>
                                    <div className="d-flex flex-column align-items-center">
                                        <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15v3c0 .5523.44772 1 1 1h9.5M3 15v-4m0 4h9m-9-4V6c0-.55228.44772-1 1-1h16c.5523 0 1 .44772 1 1v5H3Zm5 0v8m4-8v8m7.0999-1.0999L21 16m0 0-1.9001-1.9001M21 16h-5" />
                                        </svg>
                                        <span className="action-name">Referee</span>
                                    </div>
                                </div>
                                <div className="card-btn d-flex">
                                    <span className="w-50" onClick={() => navigate(`/play/${game.type}/${game.link}/referee`)}>Enter</span>
                                    <span className="w-50" onClick={() => copyToClipboard(`${window.location.origin}/play/${game.type}/${game.link}/referee`)}>Copy link</span>
                                </div>
                            </div>
                        </div>
                        {gameSettings?.snooker_shot_timer_enabled === 'true' && (
                            <div className="col-12 col-sm-6 col-md-3">
                                <div className="card cursor-pointer">
                                    <div className="card-body text-center" onClick={() => navigate(`/play/${game.type}/${game.link}/referee-shootout`)}>
                                        <div className="d-flex flex-column align-items-center">
                                            <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                                            </svg>
                                            <span className="action-name">Referee (Shoot-out)</span>
                                        </div>
                                    </div>
                                    <div className="card-btn d-flex">
                                        <span className="w-50" onClick={() => navigate(`/play/${game.type}/${game.link}/referee-shootout`)}>Enter</span>
                                        <span className="w-50" onClick={() => copyToClipboard(`${window.location.origin}/play/${game.type}/${game.link}/referee-shootout`)}>Copy link</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="col-12 col-sm-6 col-md-3">
                            <div className="card cursor-pointer position-relative">
                                <div className="card-body text-center d-flex justify-content-center align-items-center position-relative">
                                    <div className="d-flex flex-column align-items-center" onClick={() => navigate(`/streaming/${game.link}`)}>
                                        <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                            <path fillRule="evenodd" d="M14 7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7Zm2 9.387 4.684 1.562A1 1 0 0 0 22 17V7a1 1 0 0 0-1.316-.949L16 7.613v8.774Z" clipRule="evenodd" />
                                        </svg>
                                        <span className="action-name">Live stream</span>
                                    </div>
                                    <svg
                                        className="position-absolute cursor-pointer"
                                        style={{ top: '8px', right: '8px', width: '20px', height: '20px' }}
                                        aria-hidden="true"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                        onClick={() => setStreamingOpen(true)}
                                    >
                                        <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97 0-.33-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.31-.61-.22l-2.49 1c-.52-.39-1.06-.73-1.69-.98l-.37-2.65A.506.506 0 0 0 14 2h-4c-.25 0-.46.18-.5.42l-.37 2.65c-.63.25-1.17.59-1.69.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.34-.07.67-.07 1 0 .33.03.65.07.97L2.46 14.6c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.31.61.22l2.49-1c.52.39 1.06.73 1.69.98l.37 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.37-2.65c.63-.25 1.17-.59 1.69-.98l2.49 1c.22.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66Z" />
                                    </svg>
                                </div>
                                <div className="card-btn d-flex">
                                    <span className="w-50" onClick={() => navigate(`/streaming/${game.link}`)}>Enter</span>
                                    <span className="w-50" onClick={() => copyToClipboard(`${window.location.origin}/streaming/${game.link}`)}>Copy link</span>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3">
                            <div className="card cursor-pointer">
                                <div className="card-body text-center" onClick={handleOpenEditPopup}>
                                    <div className="d-flex flex-column align-items-center">
                                        <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                            <path fillRule="evenodd" d="M11.32 6.176H5c-1.105 0-2 .949-2 2.118v10.588C3 20.052 3.895 21 5 21h11c1.105 0 2-.948 2-2.118v-7.75l-3.914 4.144A2.46 2.46 0 0 1 12.81 16l-2.681.568c-1.75.37-3.292-1.263-2.942-3.115l.536-2.839c.097-.512.335-.983.684-1.352l2.914-3.086Z" clipRule="evenodd" />
                                            <path fillRule="evenodd" d="M19.846 4.318a2.148 2.148 0 0 0-.437-.692 2.014 2.014 0 0 0-.654-.463 1.92 1.92 0 0 0-1.544 0 2.014 2.014 0 0 0-.654.463l-.546.578 2.852 3.02.546-.579a2.14 2.14 0 0 0 .437-.692 2.244 2.244 0 0 0 0-1.635ZM17.45 8.721 14.597 5.7 9.82 10.76a.54.54 0 0 0-.137.27l-.536 2.84c-.07.37.239.696.588.622l2.682-.567a.492 .492 0 0 0 .255-.145l4.778-5.06Z" clipRule="evenodd" />
                                        </svg>
                                        <span className="action-name">Edit</span>
                                    </div>
                                </div>
                                <div className="card-btn d-flex">
                                    <span className="w-50" onClick={handleOpenEditPopup}>Enter</span>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3">
                            <div className="card cursor-pointer">
                                <div className="card-body text-center" onClick={() => navigate(`/queue/${game.link}`)}>
                                    <div className="d-flex flex-column align-items-center">
                                        <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 7.757v8.486M7.757 12h8.486M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                                        </svg>
                                        <span className="action-name">Queue</span>
                                    </div>
                                </div>
                                <div className="card-btn d-flex">
                                    <span className="w-50" onClick={() => navigate(`/queue/${game.link}`)}>Manage</span>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3">
                            <div className="card cursor-pointer">
                                <div className="card-body text-center" onClick={() => handleRefreshGame()}>
                                    <div className="d-flex flex-column align-items-center">
                                        <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.651 7.65a7.131 7.131 0 0 0-12.68 3.15M18.001 4v4h-4m-7.652 8.35a7.13 7.13 0 0 0 12.68-3.15M6 20v-4h4" />
                                        </svg>
                                        <span className="action-name">Refresh</span>
                                    </div>
                                </div>
                                <div className="card-btn d-flex">
                                    <span className="w-100" onClick={() => handleRefreshGame()}>Refresh Game</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={`modal modal-blur fade ${open ? 'show' : ''}`} id="modal-large" tabIndex="-1" role="dialog" aria-hidden="true" style={open ? { display: 'block' } : { display: 'none' }}>
                <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Game</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => setOpen(!open)}></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-lg-6 mb-3">
                                    <label className="mb-2 ml-2">Game name</label>
                                    <input
                                        className="form-control"
                                        type="text"
                                        placeholder="Game name"
                                        value={gameName || ''}
                                        onChange={(e) => setGameName(e.target.value)}
                                    />
                                </div>
                                <div className="col-lg-6 mb-3">
                                    <label className="mb-2 ml-2">Game type</label>
                                    <div className="form-control-plaintext">
                                        {gameType.charAt(0).toUpperCase() + gameType.slice(1).replace('-', ' ')}
                                    </div>
                                </div>
                                <div className="col-lg-12 mb-3">
                                    <label className="mb-2 ml-2">Game date</label>
                                    <input
                                        className="form-control"
                                        type="date"
                                        placeholder="Game date"
                                        value={gameDate || ''}
                                        onChange={(e) => setGameDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-lg-6 mb-3">
                                    <label className="mb-2 ml-2">Game status</label>
                                    <select
                                        className="form-control"
                                        value={gameStatus || ''}
                                        onChange={(e) => setGameStatus(e.target.value)}
                                        disabled={isDefaultGameWithLinked}
                                    >
                                        <option value="created">Created</option>
                                        <option value="live">Live</option>
                                        <option value="finished">Finished</option>
                                    </select>
                                </div>
                                {game.creation_type === 'default' ? (
                                    <div className="col-lg-6 mb-3">
                                        <label className="mb-2 ml-2">Defined matches</label>
                                        <select
                                            className="form-control"
                                            value={linkedGameId || ''}
                                            onChange={(e) => setLinkedGameId(e.target.value)}
                                        >
                                            <option value="">Default game</option>
                                            {relatedGames
                                                .filter(rg => {
                                                    return (
                                                        rg?.type === gameType &&
                                                        (rg?.status !== 'finished' || String(rg?.id) === String(linkedGameId))
                                                    );
                                                })
                                                .map(g => (
                                                    <option key={g.id} value={g.id} disabled={(String(game.linked_game_id) !== String(g.id) && g.is_linked)}>
                                                        {(g.name || 'Unnamed Game')} - {(g.first_player_name || 'Player 1')} vs {(g.second_player_name || 'Player 2')} {(String(game.linked_game_id) !== String(g.id) && g.is_linked) ? ('(Linked)') : ''}
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                ) : (
                                    <div className="col-lg-6 mb-3">
                                        <label className="mb-2 ml-2">Table</label>
                                        <select
                                            className="form-control"
                                            value={gameTable || ''}
                                            onChange={(e) => setGameTable(e.target.value)}
                                        >
                                            <option value="">Select a table</option>
                                            {tables
                                                .filter((t) => t.type === gameType)
                                                .map((table) => (
                                                    <option key={table.id} value={table.id}>
                                                        {table.name}
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                )}
                                <div className="col-lg-6 mt-3">
                                    <div className="has-flags" style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                        <Checkbox style={{ marginRight: 8 }} toggle checked={gameHasFlags === true || gameHasFlags === 1 || gameHasFlags === "1"} onChange={() => setGameHasFlags(!gameHasFlags)} />
                                        <label className="text-white">Has flags</label>
                                    </div>
                                </div>
                                <div className="col-lg-6 mt-3">
                                    <div className="is-race" style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                        <Checkbox style={{ marginRight: 8 }} toggle checked={gameIsRace === true || gameIsRace === 1 || gameIsRace === "1"} onChange={() => setGameIsRace(!gameIsRace)} />
                                        <label className="text-white">is Race</label>
                                    </div>
                                </div>
                                {(gameIsRace === true || gameIsRace === 1 || gameIsRace === "1") && (
                                    <div className="col-lg-12 mt-2">
                                        <div className="is-race-cnt" style={{ marginTop: 10 }}>
                                            <div className="race_to" style={{ width: '100%', marginTop: 10 }}>
                                                <label className="mb-2 ml-2" style={{ display: 'block', width: '100%' }}>First to</label>
                                                <input
                                                    className="form-control"
                                                    style={{ display: 'block', width: '100%' }}
                                                    type="number"
                                                    placeholder="First to"
                                                    value={gameRaceTo ?? 2}
                                                    onChange={(e) => setGameRaceTo(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <h3 className="mt-4" style={{ textAlign: 'center' }}>Players</h3>
                                <div className="col-lg-6">
                                    <label className="mb-2 ml-2" style={{ width: '100%' }}>Change the player?</label>
                                    <select
                                        className="form-control"
                                        style={{ width: '100%', margin: '5px 0px' }}
                                        value={gameFirstPlayer || ''}
                                        onChange={(e) => setGameFirstPlayer(e.target.value)}
                                    >
                                        {players.map((player) => (
                                            <option key={player.id} value={player.id}>
                                                {player.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-lg-6">
                                    <label className="mb-2 ml-2" style={{ width: '100%' }}>Change the player?</label>
                                    <select
                                        className="form-control"
                                        style={{ width: '100%', margin: '5px 0px' }}
                                        value={gameSecondPlayer || ''}
                                        onChange={(e) => setGameSecondPlayer(e.target.value)}
                                    >
                                        {players.map((player) => (
                                            <option key={player.id} value={player.id}>
                                                {player.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn me-auto" data-bs-dismiss="modal" onClick={() => setOpen(!open)}>Close</button>
                            <button type="button" className="btn btn-primary" data-bs-dismiss="modal" onClick={editGame}>Save changes</button>
                        </div>
                    </div>
                </div>
            </div>
            <div className={`modal modal-blur fade ${streamingOpen ? 'show' : ''}`} id="modal-streaming" tabIndex="-1" role="dialog" aria-hidden="true" style={streamingOpen ? { display: 'block' } : { display: 'none' }}>
                <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Streaming Settings</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => setStreamingOpen(false)}></button>
                        </div>
                        <div className="modal-body">
                            <div className="card mb-3">
                                <div className="card-body p-3 d-flex align-items-center">
                                    <div className="use-streaming-settings" style={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            style={{ marginRight: 12 }}
                                            toggle
                                            checked={useStreamingSettings}
                                            onChange={() => setUseStreamingSettings(!useStreamingSettings)}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label m-0">Use Streaming Settings</label>
                                        <div className="text-secondary small">
                                            {useStreamingSettings
                                                ? 'This table uses the style and options below.'
                                                : 'This table uses the club default. Turn this on to change it.'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <StreamingThemePicker
                                value={streamingTheme}
                                onChange={setStreamingTheme}
                                tournamentTitle={tournamentTitle}
                                onTournamentTitleChange={setTournamentTitle}
                                displayBreaks={displayBreaks}
                                onDisplayBreaksChange={setDisplayBreaks}
                                playerNameFormat={playerNameFormat}
                                onPlayerNameFormatChange={setPlayerNameFormat}
                                playerNameSize={playerNameSize}
                                onPlayerNameSizeChange={setPlayerNameSize}
                                statsMode={streamingStatsMode}
                                onStatsModeChange={setStreamingStatsMode}
                                disabled={!useStreamingSettings}
                            />
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn me-auto" data-bs-dismiss="modal" onClick={() => setStreamingOpen(false)}>Close</button>
                            <button type="button" className="btn btn-primary" data-bs-dismiss="modal" onClick={updateStreamingSettings}>Save changes</button>
                        </div>
                    </div>
                </div>
            </div>
            <div className={`modal modal-blur fade ${gameSettingsOpen ? 'show' : ''}`} id="modal-game-settings" tabIndex="-1" role="dialog" aria-hidden="true" style={gameSettingsOpen ? { display: 'block' } : { display: 'none' }}>
                <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Game Settings</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => setGameSettingsOpen(false)}></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-md-12 mb-3">
                                    <div className="use-game-settings" style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                        <Checkbox
                                            style={{ marginRight: 8 }}
                                            toggle
                                            checked={useGameSettings}
                                            onChange={() => setUseGameSettings(!useGameSettings)}
                                        />
                                        <label className="form-label text-white m-0">Use Game Settings</label>
                                    </div>
                                </div>
                                {game.type && GAME_TYPES[game.type] && (
                                    <>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Shot Timer Enabled</label>
                                            <select
                                                className="form-select"
                                                value={gameSettings[GAME_TYPES[game.type].enabledKey] || 'false'}
                                                onChange={(e) => setGameSettings({ ...gameSettings, [GAME_TYPES[game.type].enabledKey]: e.target.value })}
                                                disabled={!useGameSettings}
                                            >
                                                <option value="true">Yes</option>
                                                <option value="false">No</option>
                                            </select>
                                        </div>
                                        {game.type !== 'snooker' && (
                                            <>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Shot Time (sec)</label>
                                                    <div className="input-group">
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={gameSettings[GAME_TYPES[game.type].shotKey] || ''}
                                                            onChange={(e) => setGameSettings({ ...gameSettings, [GAME_TYPES[game.type].shotKey]: e.target.value })}
                                                            placeholder="45"
                                                            min="1"
                                                            max="300"
                                                            disabled={!useGameSettings || gameSettings[GAME_TYPES[game.type].enabledKey] !== 'true'}
                                                        />
                                                        <span className="input-group-text">seconds</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Extension Time (sec)</label>
                                                    <div className="input-group">
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={gameSettings[GAME_TYPES[game.type].extKey] || ''}
                                                            onChange={(e) => setGameSettings({ ...gameSettings, [GAME_TYPES[game.type].extKey]: e.target.value })}
                                                            placeholder="15"
                                                            min="1"
                                                            max="300"
                                                            disabled={!useGameSettings || gameSettings[GAME_TYPES[game.type].enabledKey] !== 'true'}
                                                        />
                                                        <span className="input-group-text">seconds</span>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn me-auto" data-bs-dismiss="modal" onClick={() => setGameSettingsOpen(false)}>Close</button>
                            <button type="button" className="btn btn-primary" data-bs-dismiss="modal" onClick={updateGameSettings}>Save changes</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GameDetails;
